import React, { useState, useCallback } from 'react';
import { useHistory, NavLink as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import PropTypes from 'prop-types';

import { ListItem, Button, Collapse } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import { useSelector } from 'react-redux';

import { getIsSubApp } from '~/selectors';

import NotificationLabel from '../Label/Label';
import styles from './styles';

const NOTIFICATION_ICON_COLOR = '#ab47bc';

function NavItem({
  title,
  badge,
  href,
  target,
  depth,
  children,
  icon: Icon,
  customIcon,
  className,
  open: openProp,
  label: Label,
  notificationBadge,
  ...rest
}) {
  const classes = styles();
  const history = useHistory();
  const isSubApp = useSelector(getIsSubApp);

  const [open, setOpen] = useState(openProp);

  const navigateTo = useCallback(
    (e) => {
      if (isSubApp) {
        e.preventDefault();

        const parsedPath = href.split('/');

        history.push(`/${parsedPath[parsedPath.length - 1]}`);
      }
    },
    [history, href, isSubApp]
  );

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = {
    paddingLeft,
  };

  if (children) {
    return (
      <ListItem
        {...rest}
        className={clsx(classes.item, className)}
        disableGutters
        key={title}
      >
        <Button className={classes.button} onClick={handleToggle} style={style}>
          {Icon && <Icon className={classes.icon} />}
          {title}
          {open ? (
            <ExpandLessIcon className={classes.expandIcon} color="inherit" />
          ) : (
            <ExpandMoreIcon className={classes.expandIcon} color="inherit" />
          )}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  return (
    <ListItem
      {...rest}
      className={clsx(classes.itemLeaf, className)}
      disableGutters
      key={title}
    >
      <Button
        activeClassName={classes.active}
        className={clsx(classes.buttonLeaf, `depth-${depth}`)}
        component={RouterLink}
        onClick={navigateTo}
        exact
        style={style}
        to={{
          pathname: href,
        }}
        target={target}
      >
        {Icon && <Icon className={classes.icon} />}
        {customIcon && (
          <img
            className={classes.imageIcon}
            src={customIcon}
            alt="QR_code_icon"
          />
        )}
        {title}
        {badge && (
          <span className={clsx(classes.badge, 'unreadIcon-NavItem')}>
            {badge}
          </span>
        )}
        {notificationBadge && (
          <NotificationLabel
            className={clsx(
              classes.notificationBadge,
              'notificationIcon-NavItem'
            )}
            color={NOTIFICATION_ICON_COLOR}
            shape="rounded"
          >
            {notificationBadge}
          </NotificationLabel>
        )}
        {Label && (
          <span className={classes.label}>
            <Label />
          </span>
        )}
      </Button>
    </ListItem>
  );
}

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  target: PropTypes.string,
  icon: PropTypes.shape({}),
  customIcon: PropTypes.string,
  label: PropTypes.shape({}),
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
  badge: PropTypes.string,
};

NavItem.defaultProps = {
  badge: null,
  children: null,
  open: false,
  target: '_self',
  className: '',
  href: '',
  icon: null,
  customIcon: null,
  label: null,
};

export default NavItem;
